"use strict";

import constants from "./utils/constants.js";
import utils from "./utils/utils.js";
// Global defaults
const DefaultMediaDurationMillis = constants.DefaultMediaDurationSeconds * 1000;
const DefaultMinMillis = constants.DefaultMinMinutes * 60 * 1000;
const DefaultMaxMillis = constants.DefaultMaxMinutes * 60 * 1000;
const DefaultMediaUrl = "/media/worms.gif";

// Div containing the media to display
const mediaDiv = document.getElementById("media-div");

// Global variables

let tagName;
let element;
let config;
let first = true;

// Shuffle and cycle variables for media selection
let shuffledMediaPaths = [];
let currentMediaIndex = 0;

// Duration cache for individual media files
let mediaDurationCache = {};

// Get config settings
getStreampalsConfig()
  .then((configVal) => {
    config = configVal;
    tagName = getTagNameFromFile(config.contentType);
    element = prepareElement(tagName, config);
  })
  .then(() => {
    // Create and append media element to media div
    mediaDiv.appendChild(element);
    // Get the height and width of the media element
    element.onload = () => {
      // Remove the onload event listener after the first load
      element.onload = null;
      playMediaLoop(element);
    };
  });

//#region Media Methods

// Shuffle array using Fisher-Yates algorithm
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Get cached media duration, calculating and caching if not available
async function getCachedMediaDuration(mediaPath) {
  // Return cached duration if available
  if (mediaDurationCache[mediaPath]) {
    return mediaDurationCache[mediaPath];
  }

  // Calculate and cache duration
  const fileName = mediaPath.split("/").pop();
  console.log(`Preloading duration for: ${fileName}`);

  try {
    const mediaInfo = await fetchMediaInfo(mediaPath);
    mediaDurationCache[mediaPath] = mediaInfo.duration;
    console.log(`Duration cached: ${fileName} = ${mediaInfo.duration}ms`);
    return mediaInfo.duration;
  } catch (error) {
    console.log(
      `Using default duration for ${fileName} (${DefaultMediaDurationMillis}ms)`
    );
    mediaDurationCache[mediaPath] = DefaultMediaDurationMillis;
    return DefaultMediaDurationMillis;
  }
}

// Plays media after a random delay, then hides the media after durationMillis expires, and requeues the media timer
function playMediaLoop(element) {
  let delay = utils.randomIntFromInterval(config.minDelay, config.maxDelay);
  if (first) {
    setPosition(element);
    delay = 0;
    first = false;
  }
  // Display the image after the random delay expires
  setTimeout(() => {
    // Reset image source to replay in the case of a GIF
    if (tagName === "img") {
      setMediaUrl(element, config);
    }
    // Restart video and play in the case of a WebM
    else {
      element.currentTime = 0;
      element.play();
    }
    // Make media visible
    mediaDiv.style.visibility = "visible";

    // Get duration for current media and hide after it plays
    const currentMediaPath = element.src;
    getCachedMediaDuration(currentMediaPath).then((duration) => {
      // Temporary console log for debugging
      const fileName = currentMediaPath.split("/").pop();
      console.log(`Media spawned: ${fileName} (duration: ${duration}ms)`);

      setTimeout(() => {
        mediaDiv.style.visibility = "hidden";
        setPosition(element);
        playMediaLoop(element);
      }, duration);
    });
  }, delay);
}

let lastQuadrant;

// Set position of media element on page
function setPosition(element) {
  let coordinates = utils.getMediaCoordinateStyles(
    lastQuadrant,
    element.height,
    element.width
  );
  lastQuadrant = coordinates.quadrant;
  element.style.left = coordinates.left;
  element.style.top = coordinates.top;
}

// Check if we should randomize the media
function setMediaUrl(element, config) {
  element.src = "";
  if (config.randomImage) {
    // Initialize shuffled array if empty or if we've cycled through all items
    if (
      shuffledMediaPaths.length === 0 ||
      currentMediaIndex >= shuffledMediaPaths.length
    ) {
      shuffledMediaPaths = shuffleArray(config.mediaPaths);
      currentMediaIndex = 0;
    }

    // Add cache busting parameter to force GIF restart
    const mediaPath = shuffledMediaPaths[currentMediaIndex];
    element.src = mediaPath + "?t=" + Date.now();
    currentMediaIndex++;
  } else {
    // Add cache busting parameter to force GIF restart
    element.src = config.mediaUrl + "?t=" + Date.now();
  }
}

//#endregion

//#region Streampals configuration methods

// Get Streampals config
async function getStreampalsConfig() {
  let urlParams = getUrlParams();
  let config = await createConfig(urlParams);
  return validateConfig(config);
}

function getUrlParams() {
  return new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop || ""),
  });
}

async function createConfig(urlParams) {
  const skipDelay = urlParams.skipDelay === "true";
  const maxDelayMillis = getDelayMillis(
    skipDelay,
    urlParams.max,
    DefaultMaxMillis
  );
  const minDelayMillis = getDelayMillis(
    skipDelay,
    urlParams.min,
    DefaultMinMillis
  );
  const maxHeight = isValidNumericValue(urlParams.maxHeight)
    ? urlParams.maxHeight
    : constants.DefaultMaxHeight;
  const maxWidth = isValidNumericValue(urlParams.maxWidth)
    ? urlParams.maxWidth
    : constants.DefaultMaxWidth;
  let randomImage =
    urlParams.randomImage === undefined || urlParams.randomImage === null
      ? true
      : urlParams.randomImage === "true";

  const mediaFile = await getMediaFile(urlParams.mediaUrl);
  const mediaPaths = mediaFile.mediaPaths || [];

  let mediaPath = getMediaUrl(
    mediaFile.mediaPath,
    mediaFile.mediaPaths,
    randomImage
  );

  let mediaDuration = urlParams.mediaDuration;
  // Get content type from file extension
  let extension = mediaPath.split(".").pop().split("?")[0];
  let contentType = getContentTypeFromFileExtension(extension);

  if (!isValidNumericValue(mediaDuration) || contentType === "") {
    let mediaInfo;
    try {
      mediaInfo = await fetchMediaInfo(mediaPath);
      if (contentType === "") {
        contentType = mediaInfo.contentType;
      }
      if (!isValidNumericValue(mediaDuration)) {
        mediaDuration = mediaInfo.duration;
      }
    } catch (error) {
      console.log(
        `Using default media duration of ${DefaultMediaDurationMillis} milliseconds`
      );
      mediaDuration = DefaultMediaDurationMillis;
    }
  }

  return {
    skipDelay: skipDelay,
    maxDelay: maxDelayMillis,
    minDelay: minDelayMillis,
    maxHeight: maxHeight,
    maxWidth: maxWidth,
    mediaUrl: mediaPath,
    mediaPaths: mediaPaths,
    mediaDuration: mediaDuration,
    contentType: contentType,
    randomImage: randomImage,
  };
}

async function getMediaFile(urlParamMediaUrl) {
  if (urlParamMediaUrl) {
    return { mediaPath: urlParamMediaUrl, mediaPaths: [] };
  }

  // Fetch media file
  let mediaFile = await fetch("/media/media.json")
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      return Promise.reject(res);
    })
    .catch((err) => {
      console.log(err);
    });

  return mediaFile || { mediaPath: DefaultMediaUrl, mediaPaths: [] };
}

// Get media URL
function getMediaUrl(mediaPath, mediaPaths, randomImage) {
  if (mediaPath) {
    return mediaPath;
  } else if (mediaPaths.length > 0) {
    if (randomImage) {
      // Initialize shuffled array for initial selection
      if (shuffledMediaPaths.length === 0) {
        shuffledMediaPaths = shuffleArray(mediaPaths);
        currentMediaIndex = 0;
      }
      return shuffledMediaPaths[currentMediaIndex];
    }
    return mediaPaths[0];
  }
  return DefaultMediaUrl;
}

// Validate and update config if invalid.
function validateConfig(config) {
  // minDelayMillis must be less than or equal to maxDelayMillis, else use defaults
  if (config.maxDelayMillis < config.minDelayMillis) {
    config.maxDelayMillis = DefaultMaxMillis;
    config.minDelayMillis = DefaultMinMillis;
  }

  if (config.mediaDuration === 0) {
    throw new Error(constants.ZeroMediaDurationWarning);
  }

  return config;
}

// Validate and calculate delay in milliseconds from params
function getDelayMillis(skipDelay, delayMinutes, defaultDelay) {
  if (skipDelay) return 0;

  return isValidNumericValue(delayMinutes)
    ? delayMinutes * 60 * 1000
    : defaultDelay;
}

// Check that provided string is a valid number and positive
function isValidNumericValue(numberString) {
  return (
    !isNaN(numberString) &&
    !isNaN(parseFloat(numberString)) &&
    parseFloat(numberString) > 0
  );
}

// Fetch media file
async function fetchMediaInfo(mediaUrl) {
  let contentType;
  let duration = await fetch(mediaUrl)
    .then((res) => {
      if (res.ok) {
        {
          contentType = res.headers.get("Content-Type");
          return Promise.resolve(res);
        }
      }
      return Promise.reject(res);
    })
    .then((res) => res.arrayBuffer())
    .then((ab) => calculateMediaDuration(new Uint8Array(ab)))
    .catch((err) => {
      throw new Error(
        constants.FetchImageError.replace("{0}", mediaUrl).replace(
          "{1}",
          `${err.status} - ${err.statusText}`
        )
      );
    });

  return { contentType: contentType, duration: duration };
}

// Fancy method to get Media length
function calculateMediaDuration(uint8) {
  let duration = 0;
  for (let i = 0; i < uint8.length; i++) {
    if (
      uint8[i] === 0x21 &&
      uint8[i + 1] === 0xf9 &&
      uint8[i + 2] === 0x04 &&
      uint8[i + 7] === 0x00
    ) {
      const delay = (uint8[i + 5] << 8) | uint8[i + 4];
      duration += delay < 2 ? 10 : delay;
    }
  }
  return duration * 10;
}

//#endregion

//#region Element Configuration

// Configure element to display based on tagName
function prepareElement(tagName, config) {
  // Create img or video element based on tagName
  let mediaElement = document.createElement(tagName);
  mediaElement.id = "rendered-media";
  mediaElement.style.objectFit = "contain";
  mediaElement.style.maxHeight = config.maxHeight + "%";
  mediaElement.style.maxWidth = config.maxWidth + "%";
  mediaElement.style.position = "absolute";
  mediaElement.style.top = "0";
  mediaElement.style.left = "0";
  mediaElement.alt = "Just a lil' stream pal";

  switch (tagName) {
    case "img":
      setMediaUrl(mediaElement, config);
      return mediaElement;
    case "video":
      return configureVideoElement(mediaElement, config);
    default:
      throw new Error(`Tag name ${tagName} not recognized`);
  }
}

// Map file extension to content type
function getContentTypeFromFileExtension(fileExtension) {
  switch (fileExtension) {
    case "avif":
      return "image/avif";
    case "gif":
      return "image/gif";
    case "jpeg":
    case "jpg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "svg":
      return "image/svg+xml";
    case "webp":
      return "image/webp";
    case "webm":
      return "video/webm";
    default:
      return "";
  }
}

// Get the appropriate element tag name from the media file extension
function getTagNameFromFile(contentType) {
  switch (contentType) {
    case "image/avif":
    case "image/gif":
    case "image/jpeg":
    case "image/png":
    case "image/svg+xml":
    case "image/webp":
    case contentType.startsWith("image/"):
      return "img";
    case "video/webm":
    case contentType.startsWith("video/"):
      return "video";
    default:
      {
        if (!contentType) console.warn(constants.ContentTypeNotFoundError);
        else
          console.warn(
            constants.ContentTypeNotSupportedError.replace("{0}", contentType)
          );
      }
      return "img";
  }
}

// Set Video properties
function configureVideoElement(videoElement, config) {
  let videoSource = document.createElement("source");
  videoSource.src = config.mediaUrl;
  videoSource.type = config.contentType;

  // Note: autoplay only works in Chrome after a user has interacted with the DOM unless the muted tag is used
  videoElement.autoplay = true;
  videoElement.appendChild(videoSource);

  return videoElement;
}

//#endregion
